// @flow
import './index.less';

import {Button} from 'antd';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import React, {useEffect, useMemo, useState} from 'react';
import Markdown from 'react-markdown';

import AppStoreBadges from '_components/AppStoreBadges';
import AccountConnector from '_components/CheckoutSection/AccountConnector';
import CheckoutAction from '_components/CheckoutSection/CheckoutAction';
import PaymentMethodSelector from '_components/CheckoutSection/PaymentMethodSelector';
import PlanSelector from '_components/CheckoutSection/PlanSelector';
import StepTitle from '_components/CheckoutSection/StepTitle';
import RTMCampaignDetails from '_components/RTMCampaignDetails';
import Section from '_components/Section';
import SectionTitle from '_components/SectionTitle';
import SupportCardLogos from '_components/SupportCardLogos';
import {useAuthState} from '_contexts/auth';
import {useCheckout, useCheckoutState} from '_contexts/checkout';
import useAnalytics, {event} from '_hooks/useAnalytics';
import BgImage from '_images/bg-checkout.jpg';
import BgImageRTM from '_images/bg-checkout-rtm.jpg';
import CheckIcon from '_images/icon-checkout-complete.svg';
import {checkPaymentMethodRequired} from '_utils/checkout';

type Props = {
  campaign?: string,
};

const CheckoutSection = ({campaign}: Props): React$Node => {
  const {t} = useTranslation();
  const {track} = useAnalytics();

  const {currentUser} = useAuthState();
  const {selectedPlan, promotionCode, paymentMethod, purchased} = useCheckoutState();
  const {resetPlan, resetPaymentMethod} = useCheckout();
  const [currentStep, setCurrentStep] = useState(1);

  const paymentMethodRequired = useMemo(() => {
    if (!selectedPlan) return true;

    return checkPaymentMethodRequired(selectedPlan, promotionCode);
  }, [selectedPlan, promotionCode]);

  useEffect(() => {
    setCurrentStep(v => {
      if (v === 1 && selectedPlan) {
        if (paymentMethod) return 4;
        return currentUser ? 3 : 2;
      }

      if (v === 2 && currentUser) {
        return paymentMethod ? 4 : 3;
      }

      if (v === 3 && paymentMethod) {
        return 4;
      }

      return v;
    });
  }, [selectedPlan, currentUser, paymentMethod]);

  const step1Status = useMemo(() => {
    if (!selectedPlan) {
      return 'active';
    }

    if (currentStep > 1) {
      return 'completed';
    }

    return 'default';
  }, [selectedPlan, currentStep]);

  const step1AsideNode = useMemo(() => {
    if (!selectedPlan) return null;

    return (
      <Button type="link" className="change-btn" onClick={resetPlan}>
        {t('Common_Change')}
      </Button>
    );
  }, [t, selectedPlan, resetPlan]);

  const step2Status = useMemo(() => {
    if (!currentUser) {
      return 'active';
    }

    if (currentStep > 2) {
      return 'completed';
    }

    return 'default';
  }, [currentUser, currentStep]);

  const step3AsideNode = useMemo(() => {
    if (!paymentMethod) return null;

    return (
      <Button type="link" className="change-btn" onClick={resetPaymentMethod}>
        {t('Common_Change')}
      </Button>
    );
  }, [t, paymentMethod, resetPaymentMethod]);

  const step3Status = useMemo(() => {
    if (!paymentMethod && currentUser && currentStep === 3) {
      return 'active';
    }

    if (currentStep > 3) {
      return 'completed';
    }

    return 'default';
  }, [paymentMethod, currentUser, currentStep]);

  useEffect(() => {
    if (!purchased) return;

    window.scrollTo(0, 0);
    track({
      group: event.group.Checkout,
      name: event.name.OrderComplete,
      action: event.action.Viewed,
    });
  }, [purchased, track]);

  if (purchased) {
    return (
      <Section>
        <div className="checkout-complete-container">
          <img className="complete-icon" src={CheckIcon} alt="Checkout Complete" />
          <SectionTitle className="title">{t('CheckoutSection_Complete_Title')}</SectionTitle>
          <Markdown className="description">{t('CheckoutSection_Complete_Description')}</Markdown>
          <AppStoreBadges />
          <Markdown className="footnote">{t('CheckoutSection_Complete_Footnote')}</Markdown>
        </div>
      </Section>
    );
  }

  return (
    <Section>
      <div className="checkout-container">
        <div className="center-container">
          <div className="image-col">
            <img src={campaign === 'rtm' ? BgImageRTM : BgImage} alt="checkout" />
          </div>
          <div className="content-col">
            {campaign === 'rtm' ? (
              <RTMCampaignDetails />
            ) : (
              <>
                <SectionTitle className="title">{t('CheckoutSection_Title')}</SectionTitle>
                <p className="subtitle">{t('CheckoutSection_Subtitle')}</p>
              </>
            )}

            <StepTitle
              step={1}
              title={t('CheckoutSection_StepChoosePlan')}
              status={step1Status}
              asideNode={step1AsideNode}
            />
            <PlanSelector active={step1Status === 'active'} />

            <StepTitle step={2} title={t('Common_CreateAccount')} status={step2Status} />
            <AccountConnector active={step2Status === 'active'} />

            {paymentMethodRequired && (
              <>
                <StepTitle
                  step={3}
                  title={t('CheckoutSection_StepPaymentInfo')}
                  status={step3Status}
                  asideNode={<SupportCardLogos />}
                />
                <PaymentMethodSelector active={step3Status === 'active' || step3Status === 'completed'} />
                <div className="billing-infobox">
                  <b>NOTE: </b>
                  You will not be billed until the end of your free trial. You can cancel at any time by signing into
                  your account on ritual.fit.
                </div>
              </>
            )}
            <CheckoutAction />
          </div>
        </div>
      </div>
    </Section>
  );
};

export default CheckoutSection;
